// What the phone shows of the context of a decision (Poulpe-626), read only:
// the digest, an issue's summary with its proposal and landing, and a
// session's facts and questions. No transcript, terminal, prompt, start or
// stop: the transcript stays on a computer (Fabien, Poulpe-622 record 22),
// and no phone address draws a control of an agent, a terminal or a runner.
//
// A screen opened from another names it in `from` (a question, an issue), and
// goes back there; opened directly, it goes back to Needs you. `from` is
// taken only as one of the phone's own addresses.
import type { ReactNode } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import type { Issue, Question, Session } from '../api/types'
import { Digest, useDigest } from '../board/Digest'
import { endedWhy } from '../board/stuck'
import { ChevronRightIcon, PriorityIcon, StateIcon } from '../components/Icons'
import { longAge } from '../components/Time'
import { sameKey } from '../lib/keys'
import { Markdown } from '../markdown/Markdown'
import { carriedKeys, short, stepName, waitingName } from '../queue/words'
import { endedText, sessionsOf, stateName } from '../sessions/words'
import { issueProposal } from '../store/store'
import { useBoardState } from '../store/context'
import { placeName, priorityName } from '../vocab'
import { useNeedItems } from './NeedsYou'
import { ProjectChoice, usePhoneProject } from './project'
import { askerName, headline, IssueName, Note, Screen, TopBar } from './shared'

/** Whether `key` reads as an issue's key (`Poulpe-001`). */
export const ISSUE_KEY = /^[A-Za-z][A-Za-z0-9]*-\d+$/

/** Where `from` leads back to, and its name: a question's, an issue's, a session's or the board's address on the phone, else Needs you. */
export function backOf(from: string | null): { to: string; label: string } {
  if (from && /^\/q\/[\w-]+$/.test(from)) return { to: from, label: 'Question' }
  if (from && /^\/sessions\/[\w-]+$/.test(from)) return { to: from, label: 'Session' }
  if (from && ISSUE_KEY.test(from.slice(1))) return { to: from, label: 'Issue' }
  // The board, in the view it was left in: its state, sort and filters (Poulpe-881).
  if (from && /^\/(p\/[^/?#]+\/)?board(\?[^#]*)?$/.test(from)) return { to: from, label: 'Board' }
  return { to: '/', label: 'Needs you' }
}

/** The address of this screen, as `from` names it to the next. */
function useHere(): string {
  return useLocation().pathname
}

/** The digest tab: the desktop's own digest of the project chosen, with the choice when there are several. */
export function DigestScreen() {
  const project = usePhoneProject()
  const { count } = useNeedItems()
  return (
    <Screen top={<TopBar title="Digest" back={null} brand />} tabs={{ current: 'digest', count }}>
      <ProjectChoice id="m-digest-project" />
      {project ? <ProjectDigest key={project.id} project={project.id} /> : <Note>No project yet: the digest is a project's.</Note>}
    </Screen>
  )
}

function ProjectDigest({ project }: { project: string }) {
  const { digest, error, offline, retry } = useDigest(project)
  if (offline) return <Note>Connect to read the digest: the phone keeps none.</Note>
  if (error) return <div className="m-action"><Note tone="question-error" role="alert">{error}</Note><button type="button" className="button m-target" onClick={retry}>Try again</button></div>
  if (!digest) return <Note>Reading the digest…</Note>
  return <Digest digest={digest} />
}

/** An issue's summary: where it stands, its three parts, its proposal and landing, its questions and its sessions. */
export function IssueScreen({ elsewhere }: { elsewhere: ReactNode }) {
  const { key = '' } = useParams()
  const [params] = useSearchParams()
  const here = useHere()
  const { issues, projects, proposals, returned, landings, questions, sessions } = useBoardState()
  const { count } = useNeedItems()
  const from = params.get('from')
  const back = backOf(from)
  const issue = [...issues.values()].find((i) => sameKey(i.key, key))
  // A page of the desktop's (`/board`, `/settings`) is not an issue: it is on a computer.
  if (!ISSUE_KEY.test(key)) return <>{elsewhere}</>
  const top = <TopBar title={issue?.key ?? key} back={back.to} backLabel={back.label} />
  // Opened from the digest or a link, the tab bar is the way on; from a question, its way back is.
  const tabs = from ? undefined : { current: null, count }
  if (!issue) return <Screen top={top} tabs={tabs}><Note>{key} is not on this board: it may be in another workspace, or no longer exist.</Note></Screen>
  const project = projects.get(issue.project)
  const open = issueProposal(proposals, landings, issue.id)
  const back_ = [...returned.values()].filter((p) => p.issue_id === issue.id || p.issue_ids.includes(issue.id))
  const asks = [...questions.values()].filter((q) => q.issue === issue.id && q.state === 'open')
  const list = sessionsOf(sessions, issue.id)
  return (
    <Screen top={top} tabs={tabs}>
      <section className="m-context" aria-label="About this issue">
        <div className="request-top">
          <PriorityIcon priority={issue.priority} />
          {project && <span className="request-project">{project.name}</span>}
        </div>
        <h2 className="request-title">{issue.title}</h2>
        <dl className="approval-subject m-facts">
          <dt>State</dt><dd><StateIcon state={issue.state} /> {placeName(issue, project?.columns ?? [])}</dd>
          <dt>Priority</dt><dd>{priorityName(issue.priority)}</dd>
        </dl>
      </section>
      <Part title="Description" text={issue.description} empty="No description." />
      <Part title="Plan" text={issue.plan.text} empty="No plan yet." note={issue.plan.text ? (issue.plan.approval ? 'Approved' : 'Not approved') : undefined} />
      <Part title="Outcome" text={issue.outcome} empty="No outcome yet." />
      <section className="section" aria-label="Landing">
        <h3 className="section-header">Landing</h3>
        {open ? (
          <dl className="approval-subject m-facts">
            <dt>Proposal</dt><dd>{stepName(open.proposal.step)} · <code>{short(open.proposal.head)}</code></dd>
            <dt>Carries</dt><dd>{carriedKeys(open.proposal, issues).join(', ') || issue.key}</dd>
            {open.landing && <><dt>Landing</dt><dd>{waitingName(open.landing)}</dd></>}
          </dl>
        ) : <p className="muted">No open proposal.</p>}
        {back_.map((p) => <p key={p.id} className="request-note question-changed">Returned: {(p.ended && endedWhy(p.ended)) ?? 'sent back to its session'} · <code>{short(p.head)}</code></p>)}
      </section>
      {asks.length > 0 && (
        <section className="section" aria-label="Its questions">
          <h3 className="section-header">Its questions</h3>
          <ul className="line-list">{asks.map((q) => <li key={q.id}><Link className="m-target" to={`/q/${q.id}`}>{headline(q)}</Link></li>)}</ul>
        </section>
      )}
      <section className="section" aria-label="Sessions">
        <h3 className="section-header">Sessions</h3>
        {list.length > 0 ? (
          <ul className="line-list">{list.map((s) => <li key={s.id}><SessionRow session={s} from={here} /></li>)}</ul>
        ) : <p className="muted">No sessions yet.</p>}
      </section>
    </Screen>
  )
}

function Part({ title, text, empty, note }: { title: string; text: string; empty: string; note?: string }) {
  return (
    <section className="section" aria-label={title}>
      <h3 className="section-header">{title}{note && <> <span className="muted">· {note}</span></>}</h3>
      {text.trim() ? <Markdown source={text} localImagesOnly linkTargets /> : <p className="muted">{empty}</p>}
    </section>
  )
}

function SessionRow({ session, from }: { session: Session; from: string }) {
  return (
    <Link className="m-target m-row-link" to={`/sessions/${session.id}?from=${encodeURIComponent(from)}`}>
      <span>{session.title ?? 'Untitled session'} <span className="muted">· {askerName(session)} · {stateName(session.state)}</span></span>
      <ChevronRightIcon size={14} />
    </Link>
  )
}

/** A session's facts and what it asks the person: never its transcript, terminal or controls. */
export function SessionScreen() {
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  const here = useHere()
  const { sessions, issues, questions, me } = useBoardState()
  const back = backOf(params.get('from'))
  const top = <TopBar title="Session" back={back.to} backLabel={back.label} />
  const session = sessions.get(id)
  if (!session) return <Screen top={top}><Note>This session is not on this board: it may be in another workspace, or no longer exist.</Note></Screen>
  const issue = session.issue_id ? issues.get(session.issue_id) : undefined
  const asks = [...questions.values()].filter((q: Question) => q.session === session.id && q.state === 'open' && q.target.kind === 'person' && q.target.member === me?.member?.id)
  const changes = session.changes?.files.length
  const now = Date.now()
  return (
    <Screen top={top}>
      <h2 className="request-title">{session.title ?? 'Untitled session'}</h2>
      <dl className="approval-subject m-facts">
        {issue && <><dt>Issue</dt><dd><IssueLink issue={issue} from={here} /></dd></>}
        <dt>Agent</dt><dd>{askerName(session)} · {session.agent.model}</dd>
        <dt>State</dt><dd>{stateName(session.state)}{session.ended ? ` (${endedText(session.ended)})` : ''}</dd>
        {session.awaiting_person_since && <><dt>Waits for you</dt><dd>since {longAge(session.awaiting_person_since, now)}</dd></>}
        <dt>Started</dt><dd>{longAge(session.created_at, now)}</dd>
        <dt>Last active</dt><dd>{longAge(session.last_active_at, now)}</dd>
        {changes !== undefined && <><dt>Changed files</dt><dd>{changes}</dd></>}
      </dl>
      {asks.length > 0 && (
        <section className="section" aria-label="It asks you">
          <h3 className="section-header">It asks you</h3>
          <ul className="line-list">{asks.map((q) => <li key={q.id}><Link className="m-target" to={`/q/${q.id}`}>{headline(q)}</Link></li>)}</ul>
        </section>
      )}
      <p className="muted">Its transcript, changes and terminal stay on a computer.</p>
    </Screen>
  )
}

/** An issue named as the question card names it, leading to its summary. */
export function IssueLink({ issue, from }: { issue: Issue; from: string }) {
  return <Link className="m-issue-link" to={`/${encodeURIComponent(issue.key)}?from=${encodeURIComponent(from)}`}><IssueName issue={issue} /></Link>
}
